import { useCallback, useEffect, useRef } from 'preact/hooks';
import { FaWindowClose, FaWindowRestore } from 'react-icons/fa';
import { Button } from '@/ui/components';
import { UI_HEADER_BG, UI_PANEL_BG, UI_PANEL_BORDER } from '@/ui/consts';
import { useLocale } from '@/ui/context';
import { type DialogId, useBackdropBlur, useWindowManager } from '@/ui/in-game';
import { useWidgetSize } from './use-widget-size';

type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_WIDTH: Record<DialogSize, string> = {
  sm: 'clamp(240px, 28vw, 300px)',
  md: 'clamp(350px, 36vw, 420px)',
  lg: 'clamp(380px, 48vw, 560px)',
  xl: 'clamp(480px, 64vw, 720px)',
};

type DialogBaseProps = {
  id: DialogId;
  title: string;
  children?: preact.ComponentChildren;
  size?: DialogSize;
  /** Explicit pixel width override — bypasses the size-based clamp. */
  width?: string | number;
  /** Replaces the title text with custom content (e.g. chat tabs). */
  titleContent?: preact.ComponentChildren;
  /** Hide layout/minimize/close controls (e.g. for the main chat dialog). */
  hideControls?: boolean;
  /** Disable drag handle on the title bar. */
  noDrag?: boolean;
  /** When true, limits dialog height so it doesn't overlap the hotbar. */
  avoidBottom?: boolean;
  /** When true, children are rendered without the default px-1 pb-1 padding wrapper. */
  noPadding?: boolean;
  /** Optional override for the close button action. Defaults to closeDialog(id). */
  onClose?: () => void;
  /** Allow vertical touch scrolling inside long, scrollable content. */
  touchScroll?: boolean;
};

const stopProp = (e: { stopPropagation(): void }) => e.stopPropagation();

export function DialogBase({
  id,
  title,
  children,
  size = 'md',
  width,
  titleContent,
  hideControls = false,
  noDrag = false,
  noPadding = false,
  avoidBottom = false,
  onClose,
  touchScroll = true,
}: DialogBaseProps) {
  const { locale } = useLocale();
  const {
    closeDialog,
    zIndexOf,
    getLayout,
    setLayout,
    setManualPos,
    getManualPos,
    bringToFront,
  } = useWindowManager();
  const blur = useBackdropBlur();
  const [widgetSize, setWidgetSize] = useWidgetSize(id);
  const desktop = window.matchMedia('(pointer: fine)').matches;
  const zoom = desktop && !hideControls ? widgetSize.zoom : 1;

  const containerRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{
    ptrX: number;
    ptrY: number;
    baseX: number;
    baseY: number;
  } | null>(null);

  const layout = getLayout(id);
  const manualPos = getManualPos(id);
  const isManual = layout === 'manual';

  const setManualPosRef = useRef(setManualPos);
  setManualPosRef.current = setManualPos;
  const idRef = useRef(id);
  idRef.current = id;

  useEffect(() => {
    if (!isManual) return;
    const keepVisible = () => {
      const rect=containerRef.current?.getBoundingClientRect(); if(!rect) return;
      const x=Math.max(0,Math.min(rect.left,window.innerWidth-rect.width));
      const y=Math.max(0,Math.min(rect.top,window.innerHeight-Math.min(rect.height,window.innerHeight-16)));
      if(Math.abs(x-rect.left)>.5||Math.abs(y-rect.top)>.5)setManualPosRef.current(idRef.current,x/zoom,y/zoom);
    };
    const frame=requestAnimationFrame(keepVisible);
    window.addEventListener('resize',keepVisible);
    return()=>{cancelAnimationFrame(frame);window.removeEventListener('resize',keepVisible);};
  },[zoom,isManual,id]);

  const DRAG_THRESHOLD = 4;
  const onDragPointerDown = useCallback(
    (e: PointerEvent) => {
      if (noDrag || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      bringToFront(id);
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      dragStart.current = {
        ptrX: e.clientX,
        ptrY: e.clientY,
        baseX: rect.left,
        baseY: rect.top,
      };

      const onMove = (me: PointerEvent) => {
        if (!dragStart.current) return;
        const dx = me.clientX - dragStart.current.ptrX;
        const dy = me.clientY - dragStart.current.ptrY;
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD)
          return;
        setManualPosRef.current(
          idRef.current,
          Math.max(0, dragStart.current.baseX + dx) / zoom,
          Math.max(0, dragStart.current.baseY + dy) / zoom,
        );
      };
      const onUp = () => {
        dragStart.current = null;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [id, noDrag, zoom],
  );

  const dialogWidth =
    desktop && widgetSize.width && !hideControls ? `${widgetSize.width}px` : width !== undefined
      ? typeof width === 'number'
        ? `${width}px`
        : width
      : SIZE_WIDTH[size];

  const topPx = isManual ? (manualPos?.y ?? 0) : 0;
  const maxHeightValue = avoidBottom
    ? isManual
      ? `calc((100vh - ${topPx * zoom}px - var(--hotbar-h, 64px) - 8px) / ${zoom})`
      : `calc((100dvh - var(--hotbar-h, 64px) - 48px) / ${zoom})`
    : `calc(80vh / ${zoom})`;

  const posStyle = isManual
    ? {
        position: 'fixed' as const,
        left: manualPos?.x ?? 0,
        top: manualPos?.y ?? 0,
        zIndex: zIndexOf(id),
        width: dialogWidth,
        maxWidth: `calc(90vw / ${zoom})`,
        height: desktop && !hideControls ? widgetSize.height : undefined,
        zoom,
        maxHeight: maxHeightValue,
        touchAction: touchScroll ? 'pan-y' as const : 'none' as const,
      }
    : {
        width: dialogWidth,
        maxWidth: `calc(90vw / ${zoom})`,
        height: desktop && !hideControls ? widgetSize.height : undefined,
        zoom,
        maxHeight: maxHeightValue,
        flexShrink: 0,
        touchAction: touchScroll ? 'pan-y' as const : 'none' as const,
      };

  return (
    <div
      ref={containerRef}
      role='presentation'
      data-widget={id}
      data-chat-dialog={id.startsWith('chat-') ? id : undefined}
      class={`pointer-events-auto flex flex-col overflow-hidden rounded-lg border ${UI_PANEL_BORDER} ${UI_PANEL_BG} shadow-sm ${blur}`}
      style={posStyle}
      onClick={stopProp}
      onContextMenu={stopProp}
    >
      <div
        class={`flex items-center gap-1 rounded-t-lg ${UI_HEADER_BG} px-2 py-1.5 ${noDrag ? '' : 'cursor-move'}`}
        style={{touchAction:'none'}}
        onPointerDown={onDragPointerDown}
      >
        <div class='flex min-w-0 flex-1 items-center gap-1'>
          {titleContent ?? (
            <span class='truncate px-1 font-semibold text-primary text-sm'>
              {title}
            </span>
          )}
        </div>

        {!hideControls && (
          <div class='flex shrink-0 items-center gap-1' onPointerDown={stopProp}>
            {desktop && <>
              <Button type='button' class='btn btn-ghost btn-xs' label='Zoom widget out'
                disabled={zoom <= .75}
                onClick={() => setWidgetSize(s => ({...s, zoom: Math.max(.75, Math.round((s.zoom - .1) * 100) / 100)}))}>−</Button>
              <Button type='button' class='btn btn-ghost btn-xs' label='Reset widget size'
                onClick={() => setWidgetSize({zoom: 1})}>{Math.round(zoom * 100)}%</Button>
              <Button type='button' class='btn btn-ghost btn-xs' label='Zoom widget in'
                disabled={zoom >= 3}
                onClick={() => setWidgetSize(s => ({...s, zoom: Math.min(3, Math.round((s.zoom + .1) * 100) / 100)}))}>+</Button>
            </>}
            {isManual && (
              <Button
                type='button'
                class='btn btn-ghost btn-xs'
                onClick={() => {
                  setLayout(id, 'center');
                }}
                aria-label={locale.shared.dialogRestore}
              >
                <FaWindowRestore size={13} />
              </Button>
            )}
            <Button
              type='button'
              class='btn btn-ghost btn-xs'
              onClick={() => {
                if (onClose) onClose();
                else closeDialog(id);
              }}
              aria-label={locale.shared.dialogClose}
            >
              <FaWindowClose size={13} />
            </Button>
          </div>
        )}
      </div>

      <div
        data-dialog-scroll={id}
        class={`${noPadding ? '' : 'px-1 pb-1'} min-h-0 flex-1 overflow-y-auto`}
      >
        {children}
      </div>
      {desktop && !hideControls && <div
        aria-label='Resize widget' title='Drag to resize and zoom contents' role='separator'
        style={{height: 14, flexShrink: 0, cursor: 'nwse-resize', touchAction: 'none', alignSelf: 'flex-end', width: 24, textAlign: 'right'}}
        onPointerDown={(e: PointerEvent) => {
          if (e.button !== 0) return;
          e.preventDefault(); e.stopPropagation(); bringToFront(id);
          const rect = containerRef.current?.getBoundingClientRect(); if (!rect) return;
          const x = e.clientX, y = e.clientY;
          const move = (p: PointerEvent) => {
            const dx=(p.clientX-x)/rect.width, dy=(p.clientY-y)/rect.height;
            const ratio=1+(Math.abs(dx)>Math.abs(dy)?dx:dy);
            setWidgetSize(s => ({...s, zoom: Math.max(.75, Math.min(3, Math.round(zoom * ratio * 100) / 100))}));
          };
          const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up); };
          window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); window.addEventListener('pointercancel', up);
        }}>◢</div>}
    </div>
  );
}
